import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Box } from '@customerio/pluma-components/react';
import { useMountEffect } from '@/hooks/use-mount-effect';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { useRef, type ComponentProps, type FocusEventHandler, type MouseEventHandler } from 'react';
import { gradientRingAngleVariable, gradientRingDataAttribute } from './index.css';
import * as styles from './index.css';

const BASE_GLOW_ROTATION_DEG_PER_SEC = 101;
const GLOW_ROTATION_DEG_PER_SEC = BASE_GLOW_ROTATION_DEG_PER_SEC * 0.75 * 0.75;

export function GradientRing({
  children,
  className,
  radius = 6,
  style,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...rest
}: ComponentProps<typeof Box> & {
  radius?: keyof typeof themeVars.radius;
}) {
  const glowWrapRef = useRef<HTMLDivElement>(null);
  const pointerInsideRef = useRef(false);
  const focusInsideRef = useRef(false);
  const glowRotateStartTimeRef = useRef<number | undefined>(undefined);
  const rafRef = useRef<number | undefined>(undefined);
  const prefersReducedMotionRef = useRef(false);

  useMountEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotionRef.current = mq.matches;
    const onChange = () => {
      prefersReducedMotionRef.current = mq.matches;
      if (mq.matches && rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = undefined;
        glowRotateStartTimeRef.current = undefined;
        glowWrapRef.current?.style.setProperty(gradientRingAngleVariable, '0deg');
      }
    };
    mq.addEventListener('change', onChange);
    return () => {
      mq.removeEventListener('change', onChange);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  });

  const stopGlowMotionIfIdle = () => {
    if (pointerInsideRef.current || focusInsideRef.current) return;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
    glowRotateStartTimeRef.current = undefined;
  };

  const startGlowMotionIfNeeded = () => {
    glowWrapRef.current?.style.setProperty(gradientRingAngleVariable, '0deg');
    if (prefersReducedMotionRef.current) return;
    if (rafRef.current != null) return;

    glowRotateStartTimeRef.current = performance.now();
    const loop = (now: number) => {
      if (!pointerInsideRef.current && !focusInsideRef.current) return;
      const start = glowRotateStartTimeRef.current;
      if (start === undefined) return;
      const angle = ((now - start) / 1000) * GLOW_ROTATION_DEG_PER_SEC;
      glowWrapRef.current?.style.setProperty(gradientRingAngleVariable, `${angle}deg`);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
  };

  const handleMouseEnter: MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseEnter?.(event);
    pointerInsideRef.current = true;
    startGlowMotionIfNeeded();
  };

  const handleMouseLeave: MouseEventHandler<HTMLDivElement> = (event) => {
    onMouseLeave?.(event);
    pointerInsideRef.current = false;
    stopGlowMotionIfIdle();
  };

  const handleFocus: FocusEventHandler<HTMLDivElement> = (event) => {
    onFocus?.(event);
    focusInsideRef.current = true;
    startGlowMotionIfNeeded();
  };

  const handleBlur: FocusEventHandler<HTMLDivElement> = (event) => {
    onBlur?.(event);
    const { currentTarget, relatedTarget } = event;
    if (relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) return;
    focusInsideRef.current = false;
    stopGlowMotionIfIdle();
  };

  return (
    <Box
      ref={glowWrapRef}
      {...rest}
      {...{ [gradientRingDataAttribute]: '' }}
      className={[styles.gradientContainer, className]}
      style={[style, assignInlineVars({ [styles.ringRadius]: themeVars.radius[radius] })]}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <div className={styles.gradientOverlay} aria-hidden />
      {children}
    </Box>
  );
}

export {
  gradientBackground,
  gradientHoverTarget,
  gradientRingDataAttribute,
  gradientRingGlowOpacityTransition,
  gradientRingRootFocusWithin,
  gradientRingRootGlowChild,
  gradientRingRootHover,
} from './index.css';
